import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { environment } from "../../environments/environment";
import { useAuthContext } from "../../hooks/useAuthContext";
import { EnvelopeIcon } from "@heroicons/react/24/outline";

const AlertsPage = () => {
  document.title = `Alerts | LeadZLab`;

  const [isLoading, setIsLoading] = useState(true);
  const [createAlertLoading, setCreateAlertLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [newAlertEmail, setNewAlertEmail] = useState("");
  const MAX_ALERTS = 2;
  const { user } = useAuthContext();

  const getAlerts = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${environment.BACKEND}/alerts`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      setAlerts(data.data);
    } catch (err) {
      if (err.response?.status === 400) window.alert("Please login again");
      toast.error(err?.response?.data?.msg ?? err.message, { position: "top-right" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAlerts();
  }, []);

  const handleAddAlert = async () => {
    try {
      setCreateAlertLoading(true);
      const { data } = await axios.post(
        `${environment.BACKEND}/alerts`,
        { email: newAlertEmail },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      toast.success(data.msg, { position: "top-right" });
      getAlerts();
      setNewAlertEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.msg ?? err.message, { position: "top-right" });
    } finally {
      setCreateAlertLoading(false);
    }
  };

  const handleDeleteAlert = async (id) => {
    try {
      const { data } = await axios.delete(`${environment.BACKEND}/alerts/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      toast.success(data.msg, { position: "top-right" });
      getAlerts();
    } catch (err) {
      toast.error(err?.response?.data?.msg ?? err.message, { position: "top-right" });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Alerts</h1>
          <p className="mt-1 text-slate-600">Email notifications for your scouts.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-10 rounded-lg bg-slate-200" />
            <div className="h-10 rounded-lg bg-slate-200" />
            <div className="h-24 rounded-lg bg-slate-200" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Alerts</h1>
        <p className="mt-1 text-slate-600">
          Get notified when your scouts find leads. You can add up to {MAX_ALERTS} email addresses.
        </p>
      </div>

      {alerts.length < MAX_ALERTS && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <label htmlFor="new-alert-email" className="block text-sm font-medium text-slate-700">
            Add email for alerts
          </label>
          <div className="mt-3 flex flex-wrap gap-3">
            <input
              id="new-alert-email"
              type="email"
              value={newAlertEmail}
              onChange={(e) => setNewAlertEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddAlert()}
              placeholder="you@example.com"
              className="input-base max-w-xs flex-1 min-w-[200px]"
            />
            <button
              type="button"
              onClick={handleAddAlert}
              disabled={createAlertLoading}
              className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {createAlertLoading ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      )}

      {alerts.length >= MAX_ALERTS && (
        <p className="text-sm text-amber-700">
          You’ve reached the maximum of {MAX_ALERTS} alerts. Remove one to add another.
        </p>
      )}

      {alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100">
            <EnvelopeIcon className="h-6 w-6 text-slate-500" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-slate-900">No alerts yet</h3>
          <p className="mt-2 max-w-sm text-slate-600">
            Add an email above to get notified when your scouts find new posts.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {alerts.map((alert) => (
            <div
              key={alert._id}
              className={`rounded-xl border border-slate-200 bg-white p-5 flex flex-row flex-wrap items-center justify-between gap-3 ${
                alert.active ? "ring-1 ring-emerald-200 bg-emerald-50/50" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <EnvelopeIcon className="h-5 w-5 text-slate-500" />
                <span className="font-medium text-slate-900">{alert.email}</span>
              </div>
              <button
                type="button"
                onClick={() => handleDeleteAlert(alert._id)}
                className="text-sm font-medium text-red-600 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsPage;
